import {CSSProperties, ReactElement, useMemo} from "react";
import {FaTrophy} from "react-icons/fa";
import {TournamentBracket, BracketType, TournamentTeam} from "@/competitions/tournaments/types";
import BracketRound, {BRACKET_UNIT} from "./BracketRound";
import BracketCanvas from "./BracketCanvas";
import RoundRobinStandings from "./RoundRobinStandings";
import SwissStandingsTable from "./SwissStandingsTable";
import GroupsKnockoutView from "./GroupsKnockoutView";
import "./BracketViewer.scss";


interface BracketViewerProps {
    tournamentId: string;
    bracket: TournamentBracket;
    /** Full team data array */
    teams?: TournamentTeam[];
    /** Legacy support - team ID to name mapping */
    teamNames?: Record<string, string>;
    onMatchClick?: (matchId: string) => void;
    /** The viewer's own team id — gets the cyan YOU tag through every round. */
    highlightTeamId?: string;
    /** Display options */
    showSeeds?: boolean;
    showLogos?: boolean;
    showTime?: boolean;
    compact?: boolean;
}

/**
 * Tournament bracket viewer
 * Renders single/double elimination, round robin, or swiss brackets
 * with MEL-quality polish and theming support
 */
export default function BracketViewer({
    tournamentId,
    bracket,
    teams,
    teamNames,
    onMatchClick,
    highlightTeamId,
    showSeeds = true,
    showLogos = true,
    showTime = false,
    compact = false,
}: BracketViewerProps): ReactElement {
    // Convert teams array to lookup map
    const teamsMap = useMemo(() => {
        const map: Record<string, TournamentTeam> = {};
        if (teams) {
            teams.forEach((team) => {
                map[team.id] = team;
            });
        }
        return map;
    }, [teams]);

    /*
     * Champion = winner of the final round's match. Resolve the name from the full
     * teams map when available, else the teamNames lookup (callers pass teamNames).
     */
    const championName = useMemo(() => {
        if (!bracket.rounds?.length) return null;
        const finalRound = bracket.rounds[bracket.rounds.length - 1];
        const finalMatch = finalRound?.matches?.[0];
        if (!finalMatch?.winnerId) return null;
        return teamsMap[finalMatch.winnerId]?.name || teamNames?.[finalMatch.winnerId] || null;
    }, [bracket.rounds, teamsMap, teamNames]);

    // Common props for rounds - memoized to prevent unnecessary re-renders
    const roundProps = useMemo(() => ({
        teams: teamsMap,
        teamNames,
        onMatchClick,
        highlightTeamId,
        showSeeds,
        showLogos,
        showTime,
        compact,
        tournamentId,
    }), [teamsMap, teamNames, onMatchClick, highlightTeamId, showSeeds, showLogos, showTime, compact, tournamentId]);

    /*
     * Empty state — no knockout rounds yet. GROUPS_KNOCKOUT is exempt: its
     * `rounds` (the knockout stage) are intentionally empty for the entire group
     * stage, during which GroupsKnockoutView still renders the group standings.
     * Guarding on rounds here would hide those standings for most of the
     * tournament's life.
     */
    if (bracket.type !== BracketType.GROUPS_KNOCKOUT && (!bracket.rounds || bracket.rounds.length === 0)) {
        return (
            <div className="bracket-viewer bracket-viewer--empty">
                <p>Bracket not yet generated</p>
            </div>
        );
    }


    // Double elimination - Winners, Losers, Grand Finals
    if (bracket.type === BracketType.DOUBLE_ELIMINATION && bracket.losersRounds) {
        return (
            <div className="bracket-viewer bracket-viewer--double">
                {/* Upper Bracket */}
                <section className="bracket-viewer__section bracket-viewer__section--winners">
                    <div className="bracket-viewer__section-header">
                        <span className="bracket-viewer__section-icon">🏆</span>
                        <h3 className="bracket-viewer__section-title">Upper Bracket</h3>
                    </div>
                    <BracketCanvas eyebrow={compact ? undefined : "Live Bracket"} title={compact ? undefined : "Bracket"}>
                        <div className="bracket-viewer__rounds">
                            {bracket.rounds.map((round, i) => {
                                const labelOffset = bracket.rounds
                                    .slice(0, i)
                                    .reduce((n, r) => n + r.matches.length, 0);
                                return (
                                    <BracketRound
                                        key={`w-${round.roundNumber}`}
                                        round={round}
                                        depth={i}
                                        isFinal={i === bracket.rounds.length - 1}
                                        labelOffset={labelOffset}
                                        {...roundProps}
                                    />
                                );
                            })}
                        </div>
                    </BracketCanvas>
                </section>

                {/* Flow indicator */}
                <div className="bracket-viewer__flow">
                    <div className="bracket-viewer__flow-line" />
                    <span className="bracket-viewer__flow-label">Grand Finals</span>
                    <div className="bracket-viewer__flow-line" />
                </div>

                {/* Champion slot */}
                <div className="bracket-viewer__champion">
                    <div className="bracket-viewer__champion-slot">
                        <FaTrophy className="bracket-viewer__trophy" />
                        <span className="bracket-viewer__champion-label">CHAMPION</span>
                        <span className="bracket-viewer__champion-name">
                            {championName || "TBD"}
                        </span>
                    </div>
                </div>

                {/* Lower Bracket */}
                <section className="bracket-viewer__section bracket-viewer__section--losers">
                    <div className="bracket-viewer__section-header">
                        <span className="bracket-viewer__section-icon">🔄</span>
                        <h3 className="bracket-viewer__section-title">Lower Bracket</h3>
                    </div>
                    <BracketCanvas>
                        <div className="bracket-viewer__rounds">
                            {/*
                             * Losers rounds alternate size (no clean 2^depth halving),
                             * so they keep even distribution + straight stub connectors
                             * (scoped in SCSS). Labels continue after the winners rounds.
                             */}
                            {bracket.losersRounds.map((round, i) => {
                                const winnersTotal = bracket.rounds.reduce((n, r) => n + r.matches.length, 0);
                                const labelOffset = winnersTotal + bracket.losersRounds!
                                    .slice(0, i)
                                    .reduce((n, r) => n + r.matches.length, 0);
                                return (
                                    <BracketRound
                                        key={`l-${round.roundNumber}`}
                                        round={round}
                                        labelOffset={labelOffset}
                                        {...roundProps}
                                    />
                                );
                            })}
                        </div>
                    </BracketCanvas>
                </section>
            </div>
        );
    }

    // Round robin: standings table over the full schedule
    if (bracket.type === BracketType.ROUND_ROBIN) {
        return (
            <div className="bracket-viewer bracket-viewer--round-robin">
                {bracket.roundRobinStandings?.length
                    ? <section className="bracket-viewer__section">
                        <div className="bracket-viewer__section-header">
                            <h3 className="bracket-viewer__section-title">Standings</h3>
                        </div>
                        <RoundRobinStandings
                            standings={bracket.roundRobinStandings}
                            teams={teamsMap}
                            teamNames={teamNames}
                            highlightTeamId={highlightTeamId}
                        />
                    </section> : null}
                <div className="bracket-viewer__section-header">
                    <h3 className="bracket-viewer__section-title">Round Robin Schedule</h3>
                </div>
                <div className="bracket-viewer__rounds">
                    {bracket.rounds.map((round) =>
                        <BracketRound
                            key={round.roundNumber}
                            round={round}
                            {...roundProps}
                        />)}
                </div>
            </div>
        );
    }

    // Swiss format
    if (bracket.type === BracketType.SWISS) {
        return (
            <div className="bracket-viewer bracket-viewer--swiss">
                {bracket.swissStandings?.length
                    ? <section className="bracket-viewer__section">
                        <div className="bracket-viewer__section-header">
                            <h3 className="bracket-viewer__section-title">Standings</h3>
                        </div>
                        <SwissStandingsTable
                            standings={bracket.swissStandings}
                            teams={teamsMap}
                            teamNames={teamNames}
                            highlightTeamId={highlightTeamId}
                        />
                    </section> : null}
                <div className="bracket-viewer__section-header">
                    <h3 className="bracket-viewer__section-title">Swiss Rounds</h3>
                </div>
                <div className="bracket-viewer__rounds">
                    {bracket.rounds.map((round) =>
                        <BracketRound
                            key={round.roundNumber}
                            round={round}
                            {...roundProps}
                        />)}
                </div>
            </div>
        );
    }

    // Groups → knockout (GSL style)
    if (bracket.type === BracketType.GROUPS_KNOCKOUT) {
        return (
            <GroupsKnockoutView
                bracket={bracket}
                teamNames={teamNames}
                championName={championName}
                roundProps={roundProps}
            />
        );
    }

    // Single elimination (default)
    return (
        <div className="bracket-viewer bracket-viewer--single">
            <BracketCanvas eyebrow={compact ? undefined : "Live Bracket"} title={compact ? undefined : "Bracket"}>
                <div className="bracket-viewer__rounds">
                    {bracket.rounds.map((round, i) => {
                        // Cumulative match count before this round → labels run A→…→O.
                        const labelOffset = bracket.rounds
                            .slice(0, i)
                            .reduce((n, r) => n + r.matches.length, 0);
                        return (
                            <BracketRound
                                key={round.roundNumber}
                                round={round}
                                depth={i}
                                isFinal={i === bracket.rounds.length - 1}
                                labelOffset={labelOffset}
                                {...roundProps}
                            />
                        );
                    })}

                    {/*
                     * Champion column — mockup's .round.is-champ + .bchamp card.
                     * --half = the final round's match-center offset (same 2^depth
                     * scale the rounds use), so the card centers on the final
                     * match and the connector meets it.
                     */}
                    <div
                        className="bracket-viewer__champion"
                        style={{"--half": `${BRACKET_UNIT * 2 ** (bracket.rounds.length - 1) / 2}px`} as CSSProperties}
                    >
                        <div className="bracket-viewer__round-header">
                            <h4 className="bracket-viewer__round-title">Champion</h4>
                        </div>
                        <div className="bracket-viewer__champion-slot">
                            <span className="bracket-viewer__champion-label">Cup Winner</span>
                            <span className="bracket-viewer__champion-name">
                                {championName || "TBD"}
                            </span>
                        </div>
                    </div>
                </div>
            </BracketCanvas>
        </div>
    );
}
